import React from 'react';
import { Button } from '@/components/ui/button';
import { Archive, RotateCcw } from 'lucide-react';
import { InlineConfirm } from '@/components/shared/InlineConfirm';
import { useTranslation } from '@/hooks/useTranslation';

/** Manager whose `archive.*` / `restore.*` translations label the buttons. */
type ArchiveNamespace =
  | 'traceability.articleManager'
  | 'traceability.materialManager'
  | 'traceability.processManager';

type ArchiveActionProps = {
  /** Entity currently selected in the manager; nothing is rendered without one. */
  selected: { archived: boolean } | undefined;
  i18nPrefix: ArchiveNamespace;
  archiveConfirm: boolean;
  isArchiving: boolean;
  isSaving: boolean;
  onArchiveClick: () => void;
  onArchiveConfirm: () => void;
  onArchiveCancel: () => void;
  onRestore: () => void;
};

/**
 * Archive (with inline confirmation) / restore control shared by the article, material and
 * process managers — they only differ by their translation namespace.
 */
export const ArchiveAction: React.FC<ArchiveActionProps> = ({
  selected,
  i18nPrefix,
  archiveConfirm,
  isArchiving,
  isSaving,
  onArchiveClick,
  onArchiveConfirm,
  onArchiveCancel,
  onRestore,
}) => {
  const { t } = useTranslation();

  if (!selected) return null;

  if (!selected.archived) {
    return (
      <>
        {!archiveConfirm ? (
          <Button
            variant="destructive"
            className="mt-auto w-full"
            loading={isArchiving}
            disabled={isSaving}
            onClick={onArchiveClick}
          >
            {isArchiving ? (
              t(`${i18nPrefix}.archive.archiving`)
            ) : (
              <>
                <Archive aria-hidden />
                {t(`${i18nPrefix}.archive.button`)}
              </>
            )}
          </Button>
        ) : (
          <InlineConfirm
            className="mt-auto"
            confirmLabel={t(`${i18nPrefix}.archive.confirm`)}
            cancelLabel={t('common.cancel')}
            busy={isArchiving}
            onConfirm={onArchiveConfirm}
            onCancel={onArchiveCancel}
          />
        )}
      </>
    );
  }

  return (
    <Button
      variant="outline"
      className="mt-auto w-full"
      loading={isArchiving}
      disabled={isSaving}
      onClick={onRestore}
    >
      {isArchiving ? (
        t(`${i18nPrefix}.archive.archiving`)
      ) : (
        <>
          <RotateCcw aria-hidden />
          {t(`${i18nPrefix}.restore.button`)}
        </>
      )}
    </Button>
  );
};
